import Head from 'next/head';
import { montaUrlImovelCanonica } from '../../utils/FunctionsGlobals';
import { buildBreadcrumbSchema, buildItemListSchema } from '../../utils/schema';

// Structured data da página de busca (/imoveis), renderizado via next/head (SSR-friendly,
// gerenciado pelo React) em vez do antigo RealEstateSchemaMarkup, que injetava N scripts
// no <body> com @type inválido (BuyAction/RentAction) e a MESMA URL (da busca) em todos.
// Aqui cada item do ItemList aponta para a URL CANÔNICA do imóvel (montaUrlImovelCanonica),
// idêntica à do sitemap e da tag canonical.
export default function ImoveisSchema({
  imoveis,
  site,
  domain,
  asPath,
  faq,
}: {
  imoveis: any[];
  site?: any;
  domain?: string;
  asPath?: string;
  faq?: Array<{ pergunta?: string; resposta?: string }>;
}) {
  if (!domain) return null;

  const parts = String(asPath || '').split('?')[0].split('#')[0].split('/').filter(Boolean);
  const transacao = parts[0] === 'imoveis' ? (parts[1] || '') : '';
  const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  const crumbs: Array<{ name: string; item: string }> = [
    { name: 'Início', item: `${domain}/` },
    { name: 'Imóveis', item: `${domain}/imoveis` },
  ];
  if (transacao) {
    crumbs.push({ name: cap(transacao), item: `${domain}/imoveis/${transacao}/-/-/-/-` });
  }

  const elements = (Array.isArray(imoveis) ? imoveis : [])
    .filter((item) => item?.id)
    .map((item) => ({
      url: `${domain}${montaUrlImovelCanonica(item, site)}`,
      name: item?.titulo || item?.tipo?.nome || undefined,
    }));

  // FAQPage do conteúdo SEO por cidade (CRM). Só emite quando há perguntas/respostas.
  const faqItems = (Array.isArray(faq) ? faq : []).filter((q) => q?.pergunta && q?.resposta);
  const faqSchema =
    faqItems.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map((q) => ({
            '@type': 'Question',
            name: q.pergunta,
            acceptedAnswer: { '@type': 'Answer', text: q.resposta },
          })),
        }
      : null;

  return (
    <Head>
      <script
        type="application/ld+json"
        key="imoveis-breadcrumb"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildBreadcrumbSchema(crumbs)) }}
      />
      {elements.length > 0 ? (
        <script
          type="application/ld+json"
          key="imoveis-itemlist"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildItemListSchema(elements)) }}
        />
      ) : null}
      {faqSchema ? (
        <script
          type="application/ld+json"
          key="imoveis-faq"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      ) : null}
    </Head>
  );
}
